import React from 'react';
import PropTypes from 'prop-types';
import { formatBytes, formatCores } from '../../services/SandboxService';
import './sandbox.css';

const pct = (used, limit) => (limit ? Math.min(100, Math.round((Number(used || 0) / limit) * 100)) : null);

// What "CPU now" and "Memory now" count: the whole sandbox, which is what the plan's limit caps (vfs-service's
// overview: workspace + previews; the gateway puts the plan on the pod as its total).
const WHOLE_SANDBOX = 'Workspace and previews';

/**
 * The plan's limits next to what the sandbox uses now. A meter without a limit shows only the value; one at 90% or
 * more of its limit is drawn as a warning. CPU and memory above the plan (a reading taken while the sandbox is being
 * held back to it) say so instead of looking like a broken limit.
 */
export function buildMeters(sandbox, { disk = null } = {}) {
  const usage = sandbox?.usage || {};
  const limits = sandbox?.plan?.limits || {};
  const cores = usage.cpuPercent === null || usage.cpuPercent === undefined ? null : (usage.cpuPercent / 100);
  return [
    { key: 'cpu', label: 'CPU now', used: cores, limit: limits.cpu, format: (v) => (v === null ? '—' : `${v.toFixed(1)}`), unit: limits.cpu ? `${limits.cpu} vCPU` : 'vCPU', note: WHOLE_SANDBOX,
      over: (limit) => `Briefly above ${limit} vCPU: your sandbox is slowed to its plan` },
    { key: 'memory', label: 'Memory now', used: usage.memoryBytes, limit: limits.memoryBytes, format: formatBytes, note: WHOLE_SANDBOX,
      over: () => 'Above the plan: the biggest process may be stopped' },
    disk
      ? { key: 'disk', label: 'Disk', used: disk.used, limit: disk.limit, format: formatBytes, note: 'Code and data' }
      : { key: 'disk', label: 'Disk', used: usage.diskBytes, limit: limits.diskBytes, format: formatBytes, note: 'Projects and history' },
    { key: 'services', label: 'Services running', used: usage.runningServices, limit: limits.runningServices, format: (v) => String(v ?? 0), note: 'At once' },
    { key: 'hours', label: 'Active time', used: usage.activeHoursThisMonth, limit: limits.activeHoursPerMonth, format: (v) => (v === null || v === undefined ? '—' : `${v} h`), note: 'This month' },
  ].filter((meter) => meter.key !== 'hours' || meter.limit || meter.used !== null);
}

/** A volume's size as the stats give it: the smaller of its target and its real size, else whichever is known. */
export const volumeSize = (volume) => (volume?.target && volume?.size ? Math.min(volume.target, volume.size) : (volume?.size || volume?.target || null));

/**
 * The sandbox's disk as ONE number: the code volume plus the data volume (the person's stats, MySandboxStats), over
 * both sizes when every volume's size is known. Null without the stats' disk: the meter then shows the overview's.
 */
export function diskTotal(disk) {
  const volumes = [disk?.code, disk?.data].filter(Boolean);
  if (volumes.length === 0 || !volumes.some((volume) => typeof volume.used === 'number')) { return null; }
  const used = volumes.reduce((sum, volume) => sum + Number(volume.used || 0), 0);
  const sizes = volumes.map(volumeSize);
  return { used, limit: sizes.every(Boolean) ? sizes.reduce((sum, size) => sum + size, 0) : null };
}

/** The note under a meter: what it counts, or why it is at or above its limit. */
export function meterNote(meter) {
  const value = pct(meter.used, meter.limit);
  if (meter.over && meter.limit && Number(meter.used) > meter.limit) { return { text: meter.over(meter.limit), warn: true }; }
  const warn = value !== null && value >= 90;
  if (warn && meter.key === 'services') { return { text: 'At the limit: stop one to start another', warn }; }
  return { text: meter.note, warn };
}

/** "Counted: workspace 0.10 vCPU · 310 MB, previews 0.50 vCPU · 1.1 GB, 3 running": what makes up the meters. */
export function UsageBreakdown({ sandbox }) {
  const usage = sandbox?.usage;
  if (!usage) { return null; }
  const { workspace, previews } = usage;
  return (
    <span className="sbx-home-split">
      Counted:{' '}
      {workspace ? (
        <span title="Flow's own service in your sandbox and what it runs: shell commands, git, installs">
          workspace {formatCores(workspace.cpuPercent)} · {formatBytes(workspace.memoryBytes)},{' '}
        </span>
      ) : null}
      <span title="The services you run, each with its build and dev server">
        previews {formatCores(previews?.cpuPercent)} · {formatBytes(previews?.memoryBytes)}
      </span>
      , {usage.runningServices ?? 0} running
    </span>
  );
}

UsageBreakdown.propTypes = { sandbox: PropTypes.object };

/**
 * `plain`: Home's usage card — no box around each meter, and the note only when it warns. `disk`: diskTotal()'s
 * answer, in place of the overview's disk.
 */
export default function UsageMeters({ sandbox, only = null, plain = false, disk = null }) {
  const meters = buildMeters(sandbox, { disk }).filter((meter) => !only || only.includes(meter.key));
  return (
    <div className={`sbx-meters${only ? ' sbx-meters-compact' : ''}${plain ? ' sbx-meters-plain' : ''}`}>
      {meters.map((meter) => {
        const value = pct(meter.used, meter.limit);
        const note = meterNote(meter);
        const limitText = meter.limit ? (meter.unit || (meter.key === 'services' ? String(meter.limit) : meter.format(meter.limit))) : null;
        return (
          <div className="sbx-meter" key={meter.key}>
            <span className="sbx-meter-label">{meter.label}</span>
            <span className="sbx-meter-value">
              {meter.format(meter.used ?? null)}
              {limitText ? <span className="sbx-meter-limit"> / {limitText}</span> : null}
            </span>
            {value !== null && (
              <span aria-label={`${meter.label}: ${value}% of the limit`} className="sbx-bar" role="img">
                <span className={`sbx-bar-fill${note.warn ? ' warn' : ''}`} style={{ width: `${value}%` }} />
              </span>
            )}
            {(!plain || note.warn) && <span className={`sbx-meter-note${note.warn ? ' warn' : ''}`}>{note.text}</span>}
          </div>
        );
      })}
    </div>
  );
}

UsageMeters.propTypes = {
  sandbox: PropTypes.object,
  only: PropTypes.arrayOf(PropTypes.string),
  plain: PropTypes.bool,
  disk: PropTypes.shape({ used: PropTypes.number, limit: PropTypes.number }),
};
